import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Star, Quote } from 'lucide-react';

const TestimonialsSection = () => {
  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: true,
    rootMargin: '50px 0px', // Start animation when component is 50px before entering viewport
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const testimonialVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const testimonials = [
    {
      id: 1,
      image: "/images/recommendations/המלצה 1.jpg",
      alt: "המלצה 1"
    },
    {
      id: 2,
      image: "/images/recommendations/המלצה 2.jpg",
      alt: "המלצה 2"
    },
    {
      id: 3,
      image: "/images/recommendations/המלצה 3.jpg",
      alt: "המלצה 3"
    },
    {
      id: 4,
      image: "/images/recommendations/המלצה 4.jpg",
      alt: "המלצה 4"
    }
  ];

  return (
    <section className="section-padding bg-gradient-to-br from-dream-soft-beige via-dream-light-beige to-dream-pink-beige relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 left-10 w-40 h-40 bg-gradient-to-r from-dream-teal to-dream-warm-brown rounded-full blur-3xl opacity-15"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-gradient-to-r from-dream-pink-beige to-dream-soft-beige rounded-full blur-2xl opacity-20"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-gradient-to-r from-dream-warm-brown to-dream-teal rounded-full blur-3xl opacity-10"></div>
      </div>

      <motion.div
        ref={ref}
        className="container-width relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="flex gap-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="text-dream-warm-brown fill-current" size={24} />
              ))}
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            <span className="gradient-text">לקוחות ממליצים</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            מאות לקוחות חוו את כוח הסדנה שלנו ושיתפו את החוויה המטרפת והשינוי החיובי שעברו
          </p>

          <div className="flex justify-center">
            <Quote className="text-dream-teal" size={32} />
          </div>
        </motion.div>

        <motion.div 
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto"
        >
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              variants={testimonialVariants}
              className="group"
            >
              <div className="bg-white bg-opacity-80 backdrop-blur-sm p-6 rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 border border-white border-opacity-50">
                <div className="relative overflow-hidden rounded-2xl">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.alt}
                    loading="lazy"
                    className="w-full h-auto object-contain transition-transform duration-300 group-hover:scale-105"
                    style={{ maxHeight: '600px', minHeight: '300px' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default TestimonialsSection;
